const ISO_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

export function normalizeLookupKey(value) {
    if (value == null) return [];
    const trimmed = String(value).trim().toLowerCase();
    if (!trimmed) return [];
    const ascii = trimmed.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    return Array.from(new Set([trimmed, ascii].filter(Boolean)));
}

export function buildLookupKeys(...values) {
    const set = new Set();
    values.forEach((value) => {
        normalizeLookupKey(value).forEach((key) => set.add(key));
    });
    return Array.from(set);
}

export function resolveEmployeeRecipient(staffName, staffId, employees = []) {
    const targetName = String(staffName || '').trim();
    const targetId = String(staffId || '').trim();
    const nameLower = targetName.toLowerCase();
    const idLower = targetId.toLowerCase();
    const list = Array.isArray(employees) ? employees : [];

    const match = list.find((emp) => {
        if (!emp) return false;
        if (typeof emp === 'string') {
            const value = emp.trim().toLowerCase();
            return value === nameLower || (idLower && value === idLower);
        }
        const candidates = [emp.login, emp.id, emp.name]
            .filter(Boolean)
            .map((value) => String(value).trim().toLowerCase());
        return candidates.includes(nameLower) || (idLower && candidates.includes(idLower));
    });

    if (!match) {
        return {
            mapped: false,
            staffName: targetName,
            staffId: targetId || null,
            userId: null,
            lookupKeys: [],
        };
    }

    // NIEPEWNE: tymczasowi nie dostają powiadomień (brak konta / logowania).
    if (match && typeof match === 'object' && (match.isTemporary === true || match.archived === true)) {
        return {
            mapped: false,
            staffName: targetName || match.name || '',
            staffId: targetId || match.id || null,
            userId: null,
            lookupKeys: [],
        };
    }

    if (typeof match === 'string') {
        return {
            mapped: true,
            staffName: match,
            staffId: match,
            userId: match,
            lookupKeys: buildLookupKeys(match),
        };
    }

    const userId = match.login || match.id || match.name || null;
    if (!userId) {
        return {
            mapped: false,
            staffName: targetName || match.name || '',
            staffId: targetId || match.id || null,
            userId: null,
            lookupKeys: [],
        };
    }

    return {
        mapped: true,
        staffName: match.name || targetName,
        staffId: match.id || match.login || userId,
        userId,
        lookupKeys: buildLookupKeys(match.login, match.id, match.name, targetName, targetId),
    };
}

/** Osoba spoza stałej listy (w tym zapisany tymczasowy) nie dostaje pusha, maila ani wpisu w kolejce. */
export function shouldEnqueueStaffNotification(staffName, employees = []) {
    return resolveEmployeeRecipient(staffName, null, employees).mapped === true;
}

export function parseLocalIsoDate(iso) {
    if (!iso) return null;
    const match = String(iso).match(ISO_DAY_RE);
    if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
    const parsed = Date.parse(iso);
    return Number.isFinite(parsed) ? new Date(parsed) : null;
}

export function formatLocalDateLabel(iso, options = { day: 'numeric', month: 'long', year: 'numeric' }) {
    const date = parseLocalIsoDate(iso);
    if (!date) return iso || '';
    return date.toLocaleDateString('pl-PL', options);
}

export function shouldDisplayPushNotification(payload = {}) {
    const data = payload.data || payload;
    const tag = payload.tag || data.tag;
    return !(data?.type === 'data_update' || data?.silent === true || tag === 'data-update');
}

/** Dla tej samej osoby + wydarzenia + dnia zostaje ostatni wpis (add/remove/propose). */
export function netNotificationsByPersonDate(notifications = []) {
    const byKey = new Map();
    notifications.forEach((item) => {
        const key = `${item.userId || item.staffId || item.staffName}::${item.eventName}::${item.date}`;
        const prev = byKey.get(key);
        if (!prev || String(item.timestamp || '') >= String(prev.timestamp || '')) {
            byKey.set(key, item);
        }
    });
    return Array.from(byKey.values());
}
